import { useState } from "react";
import { FlaskConical, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useTokenData } from "@/context/TokenDataContext";
import { formatCompactUsd } from "@/lib/format";

const SIMULATED_UNLOCKED_CAP = 12_500_000;
const SLIDER_MAX = 25_000_000;

export const DevPanel = () => {
  const enabled = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("dev") === "1";
  const [open, setOpen] = useState(true);
  const { token, liveMarketCap, marketCap, isUnlocked, isSimulated, simulatedMarketCap, setSimulatedMarketCap, unlockMarketCap } = useTokenData();

  if (!enabled) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] grid h-11 w-11 place-items-center rounded-full border border-cyan2/60 bg-ink/90 text-cyan2 shadow-[0_0_20px_rgba(0,240,255,0.4)]"
        aria-label="Open dev panel"
        data-testid="dev-panel-open-button"
      >
        <FlaskConical className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="glass fixed bottom-5 right-5 z-[60] w-[300px] rounded-xl border-cyan2/40 p-4 font-body text-sm" data-testid="dev-panel">
      <div className="flex items-center justify-between">
        <div className="font-display text-[0.62rem] font-bold tracking-[0.2em] text-cyan2">DEV · UNLOCK SIMULATOR</div>
        <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white" aria-label="Close dev panel" data-testid="dev-panel-close-button">
          <X className="h-4 w-4" />
        </button>
      </div>

      <label className="mt-4 flex items-center justify-between gap-3">
        <span className="font-semibold text-white/85">Simulate ≥ {formatCompactUsd(unlockMarketCap)} market cap</span>
        <Switch
          checked={isSimulated && simulatedMarketCap >= unlockMarketCap}
          onCheckedChange={(v) => setSimulatedMarketCap(v ? SIMULATED_UNLOCKED_CAP : null)}
          className="border-white/20 data-[state=checked]:bg-neon data-[state=unchecked]:bg-white/15"
          data-testid="dev-simulate-unlock-switch"
        />
      </label>

      <div className="mt-4">
        <div className="flex justify-between font-mono text-[0.65rem] text-white/55">
          <span>SIMULATED MARKET CAP</span>
          <span data-testid="dev-slider-value">{isSimulated ? formatCompactUsd(simulatedMarketCap) : "OFF"}</span>
        </div>
        <input
          type="range"
          min={0}
          max={SLIDER_MAX}
          step={100_000}
          value={simulatedMarketCap ?? liveMarketCap ?? 0}
          onChange={(e) => setSimulatedMarketCap(Number(e.target.value))}
          className="mt-2 w-full accent-[#00FF66]"
          data-testid="dev-market-cap-slider"
        />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-y-1.5 font-mono text-[0.68rem]">
        <dt className="text-white/50">SOURCE ({token?.source ?? "—"})</dt>
        <dd className="text-right text-white" data-testid="dev-live-marketcap">{formatCompactUsd(liveMarketCap)}</dd>
        <dt className="text-white/50">EFFECTIVE</dt>
        <dd className="text-right text-white" data-testid="dev-effective-marketcap">{formatCompactUsd(marketCap)}</dd>
        <dt className="text-white/50">isUnlocked</dt>
        <dd className={`text-right font-bold ${isUnlocked ? "text-neon" : "text-gold"}`} data-testid="dev-is-unlocked">{String(isUnlocked)}</dd>
      </dl>

      <button
        type="button"
        onClick={() => setSimulatedMarketCap(null)}
        className="mt-4 w-full rounded-md border border-white/15 py-2 font-display text-[0.62rem] font-bold tracking-widest text-white/80 transition-colors hover:border-neon/60 hover:text-neon"
        data-testid="dev-reset-button"
      >
        RESET TO SOURCE DATA
      </button>
    </div>
  );
};
